<template>
  <loading-wrapper :is-loading="!projectData">
    <div id="container">
      <div id="title-container">
        <h1>{{ projectTitle }}</h1>
        <vf-button
          :variant="buttonVariants.secondary"
          @click="() => goToProjectRoom()"
          >{{ t('projectDetails.goToProjectRoom') }}</vf-button
        >
      </div>

      <PVTabs
        v-if="store.state.userRoles?.length && projectData"
        v-model:value="activeTab"
        lazy
      >
        <PVTabList>
          <router-link :to="getTabLink(ProjectDetailsTab.SETTINGS)">
            <PVTab
              id="ddp-project-details-tab-list-settings"
              :value="ProjectDetailsTab.SETTINGS"
            >
              {{ t('projectDetails.tabs.settings') }}
            </PVTab>
          </router-link>
          <router-link :to="getTabLink(ProjectDetailsTab.SUPPLIERS)">
            <PVTab
              v-if="isUserRole(suppliersAllowedRoles)"
              id="ddp-project-details-tab-list-suppliers"
              :value="ProjectDetailsTab.SUPPLIERS"
            >
              {{ t('projectDetails.tabs.suppliers') }}
            </PVTab>
          </router-link>
          <router-link :to="getTabLink(ProjectDetailsTab.UPLOADS)">
            <PVTab
              id="ddp-project-details-tab-list-deliveries"
              :value="ProjectDetailsTab.UPLOADS"
            >
              {{ t('projectDetails.tabs.deliveries') }}
            </PVTab>
          </router-link>
          <router-link :to="getTabLink(ProjectDetailsTab.DOCUMENTS)">
            <PVTab
              id="ddp-project-details-tab-list-documents"
              :value="ProjectDetailsTab.DOCUMENTS"
            >
              {{ t('projectDetails.tabs.documents') }}
            </PVTab>
          </router-link>
          <router-link :to="getTabLink(ProjectDetailsTab.IBL)">
            <PVTab
              v-if="isUserRole(iblAllowedRoles)"
              :value="ProjectDetailsTab.IBL"
            >
              {{ t('projectDetails.tabs.ibl') }}
            </PVTab>
          </router-link>
        </PVTabList>
        <PVTabPanels>
          <PVTabPanel :value="ProjectDetailsTab.SETTINGS">
            <project-informations
              :project-data="projectData"
              @project-updated="fetchProjectData"
            />
          </PVTabPanel>
          <PVTabPanel :value="ProjectDetailsTab.SUPPLIERS">
            <projects-suppliers-list
              :project-data="projectData"
              @update-project="fetchProjectData"
            />
          </PVTabPanel>
          <PVTabPanel :value="ProjectDetailsTab.UPLOADS">
            <uploads-overview
              :project-id="id"
              :project-number="projectData.projectNumber"
              :project-title="projectData.projectTitle"
            />
          </PVTabPanel>
          <PVTabPanel :value="ProjectDetailsTab.DOCUMENTS">
            <files-overview-table :project-id="id" />
          </PVTabPanel>
          <PVTabPanel :value="ProjectDetailsTab.IBL">
            <ibl-view :project-id="id" :project-type="projectType" />
          </PVTabPanel>
        </PVTabPanels>
      </PVTabs>
    </div>
  </loading-wrapper>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { useRouter, RouteLocationRaw } from 'vue-router';
  import { useStore } from 'vuex';
  import { useI18n } from 'vue-i18n';
  import { ProjectType, Role, IProjectDetailsDto } from 'types';
  import FilesOverviewTable from '../../components/FilesOverviewTable/FilesOverviewTable.vue';
  import ProjectInformations from '../../components/ProjectInformations/ProjectInformations.vue';
  import ProjectsSuppliersList from '../../components/ProjectsSuppliersList/ProjectsSuppliersList.vue';
  import UploadsOverview from '../../components/UploadsOverview/UploadsOverview.vue';
  import IblView from '@/components/IblView/IblView.vue';
  import { useToast } from '../../composables/useToast';
  import { ProjectDetailsTab } from './ProjectDetails.types';
  import { Route, routeParams } from '../../routes';
  import { ProjectDataService, RedirectionService } from '@/services';
  import { useTabs } from '@/composables/useTabs';

  const props = defineProps<{
    id: string;
    referenceNumberForFilesFiltering?: string;
  }>();

  const { t } = useI18n();
  const router = useRouter();
  const store = useStore();
  const { createErrorToast } = useToast();

  const iblAllowedRoles = [Role.Admin];
  const projectData = ref<IProjectDetailsDto | undefined>(undefined);
  const suppliersAllowedRoles = [Role.Admin, Role.DocuManager];

  const projectType = computed(() => ProjectType.SUPPLIER_PROJECT);

  const { activeTab } = useTabs(
    Route.PROJECT_SUPPLIER_DETAILS,
    ProjectDetailsTab,
    ProjectDetailsTab.SETTINGS,
  );
  function getTabLink(tab: ProjectDetailsTab): RouteLocationRaw {
    return {
      name: Route.PROJECT_DETAILS,
      params: {
        [routeParams[Route.PROJECT_DETAILS].projectId]: props.id,
        [routeParams[Route.PROJECT_DETAILS].tab]: tab,
      },
    };
  }

  async function fetchProjectData() {
    try {
      const project = await ProjectDataService.getSupplierProjectData(props.id);
      projectData.value = project;
    } catch {
      createErrorToast(t('errors.error'));
    }
  }

  async function goToProjectRoom() {
    if (!projectTitle.value) return;
    const url = await RedirectionService.getProjectRoomUrl(projectTitle.value);
    window.open(url, '_blank');
  }

  const projectTitle = computed(() => projectData.value?.projectTitle || '');

  onMounted(() => {
    if (props.id === undefined) {
      router.push({ name: Route.PROJECTS_LIST });
      return;
    }
    fetchProjectData();
  });
</script>

<style scoped lang="scss">
  #container {
    #title-container {
      margin-bottom: 4.25rem;
      display: flex;
      align-items: center;
      justify-content: space-between;

      h1 {
        font-weight: 700;
        font-size: 50px;
        line-height: 36px;
      }
    }
  }
</style>
